/**
 * Новые категории из чеков и выписок (15.10.2026, docs/design/newcats-2026-10-15).
 *
 * Покупке, которой в справочнике нет места, модель предлагает новую
 * категорию: «Спорт» для абонемента, «Декор» для свечи. Раньше такая покупка
 * молча уходила в «Прочее» или в первую попавшуюся категорию, и «Аналитика»
 * показывала не то.
 *
 * Как поступать, человек решает один раз: создавать автоматически,
 * спрашивать каждый раз или класть в «Прочее». Ответ хранится в настройках и
 * едет со снимком; изменить его можно в «Ещё → Категории».
 *
 * До «Записать» новой категории нет: она живёт в проверке под ключом
 * `new:<название>` и создаётся вместе с операциями. Брошенная проверка
 * справочник не трогает, а категория, у которой не осталось ни одной
 * записываемой операции, не появляется вовсе.
 */
import { useMemo, useRef, useState } from 'react';
import type { PlanPart } from '@skoka/core';
import { suggestCategories, type CategorySuggestion } from './ai.js';
import { modelCatalog, NEW_CATEGORY, isNewCategory, type Category, type NewCategoryMode } from './categories.js';
import { categoryKey, saveCategory } from './category-store.js';
import { getAll } from './db.js';
import { PACK_ICONS } from './icon-pack.js';
import { hasIcon, Icon } from './icon.js';
import { CategoryGlyph, colorOf } from './category-icon.js';
import { CategoryField } from './operation-fields.js';
import { SheetWrap } from './sheet.js';
import { setNewCategoryMode } from './store.js';
import { plural } from './text.js';

/** Новая категория до «Записать». `restoreId` — архивная с тем же названием: её вернём, а не заведём вторую. */
export type Proposal = Category & { defaultPart: PlanPart; restoreId?: string };

/** Значки, из которых выбирает модель: те, что есть у категорий в приложении. */
export const SUGGEST_ICONS: string[] = [...new Set(PACK_ICONS.map((i) => i.name))];

export const MODE_LABEL: Record<NewCategoryMode, string> = {
  auto: 'Создавать автоматически',
  ask: 'Спрашивать каждый раз',
  other: 'Класть в «Прочее»',
};
const MODE_SUB: Record<NewCategoryMode, string> = {
  auto: 'новые категории появятся сами — перед записью их видно, и любую можно заменить своей',
  ask: 'перед записью будет предложение создать — одним тапом',
  other: 'новых категорий не будет',
};
const PART_WORD: Partial<Record<PlanPart, string>> = { need: 'повседневные нужды', want: 'свободные траты' };

/** Сохранённый ответ; чужое значение — как «ещё не отвечал». */
export const modeOf = (v: unknown): NewCategoryMode | undefined =>
  (v === 'auto' || v === 'ask' || v === 'other' ? v : undefined);

/** «World Class, ЛитРес и Ozon»; длинный список — первые два и «ещё N». */
export function namesText(names: readonly string[]): string {
  const uniq = [...new Set(names)];
  if (uniq.length > 3) return `${uniq.slice(0, 2).join(', ')} и ещё ${uniq.length - 2}`;
  return uniq.length > 1 ? `${uniq.slice(0, -1).join(', ')} и ${uniq.at(-1)}` : uniq[0] ?? '';
}

/**
 * Предложения модели → категории проверки.
 *
 * Название, которое уже есть среди действующих категорий, — это она и есть:
 * второй «Спорт» не нужен. Архивная с тем же названием возвращается из
 * архива. Остальные становятся новыми — одна на название, сколько бы покупок
 * её ни получили. Вкладывается новая только в действующую категорию верхнего
 * уровня, а у подкатегории — в её корень. Часть плана — от родителя, если она
 * у него своя; значок — из набора, иначе родительский.
 */
export function planSuggestions(
  suggestions: readonly (CategorySuggestion | null)[], categories: readonly Category[],
): { ids: (string | null)[]; proposals: Proposal[] } {
  const expense = categories.filter((c) => (c.kind ?? 'expense') === 'expense');
  const byId = new Map(categories.map((c) => [c.id, c]));
  const rootOf = (id: string): Category | undefined => {
    let c = byId.get(id);
    for (let i = 0; c?.parentId && i < 5; i++) c = byId.get(c.parentId);
    return c;
  };
  const proposals = new Map<string, Proposal>();
  const ids = suggestions.map((s) => {
    if (!s) return null;
    const key = categoryKey(s.name);
    if (!key) return null;
    const live = expense.find((c) => !c.archived && categoryKey(c.name) === key);
    if (live) return live.id;
    const id = `${NEW_CATEGORY}${key}`;
    if (!proposals.has(id)) {
      const root = s.parentId ? rootOf(s.parentId) : undefined;
      const parent = root && !root.archived && (root.kind ?? 'expense') === 'expense' ? root : undefined;
      const old = expense.find((c) => c.archived && categoryKey(c.name) === key);
      const oldParent = old?.parentId ? byId.get(old.parentId) : undefined;
      const parentId = old && oldParent && !oldParent.archived ? oldParent.id : parent?.id ?? null;
      const host = parentId ? byId.get(parentId) : undefined;
      const part = old?.defaultPart ?? host?.defaultPart ?? s.part;
      const icon = old?.icon
        || (s.icon !== 'shapes' && hasIcon(s.icon) ? s.icon : host && hasIcon(host.icon) ? host.icon : 'shapes');
      proposals.set(id, {
        id, kind: 'expense', name: old?.name ?? s.name, parentId, defaultPart: part, icon,
        color: old?.color ?? (host ? colorOf(host) : colorOf({ defaultPart: part })),
        ...(old ? { restoreId: old.id } : {}),
      });
    }
    return id;
  });
  return { ids, proposals: [...proposals.values()] };
}

/**
 * Создать новые категории записываемых операций и вернуть настоящие ключи
 * вместо `new:`. Категория, которую тем временем завели в другом месте, не
 * дублируется: берётся она.
 */
export async function createProposals(used: readonly Proposal[]): Promise<Map<string, string>> {
  const real = new Map<string, string>();
  for (const p of used) {
    try {
      const c = await saveCategory({
        ...(p.restoreId ? { id: p.restoreId, archived: false } : {}),
        name: p.name, kind: 'expense', defaultPart: p.defaultPart, parentId: p.parentId, icon: p.icon,
        ...(p.color ? { color: p.color } : {}),
      });
      real.set(p.id, c.id);
    } catch (e) {
      const all = await getAll<Category>('categories');
      const same = all.find((c) => (c.kind ?? 'expense') === 'expense' && !c.archived
        && categoryKey(c.name) === categoryKey(p.name));
      if (!same) throw e;
      real.set(p.id, same.id);
    }
  }
  return real;
}

/**
 * Новые категории одной проверки: вопрос, предложения и их судьба.
 *
 * Строки проверки — чека или выписки — экран держит сам; сюда приходит
 * категория каждой строки и способ её поменять.
 */
export function useNewCategories(p: {
  mode: unknown;
  categories: Category[];
  onCategories: (c: Category[]) => void;
  /** Категория каждой строки сейчас. */
  ids: readonly string[];
  /** Строку человек уже решил сам: пришедшее позже предложение её не трогает. */
  touched: (n: number) => boolean;
  /** Поставить строкам категории. */
  apply: (changes: ReadonlyMap<number, string>) => void;
}) {
  const [mode, setMode] = useState(modeOf(p.mode));
  const [asking, setAsking] = useState(false);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  /** Строки, которым предложена новая категория, а решения нет: строка → ключ. */
  const [pending, setPending] = useState<ReadonlyMap<number, string>>(new Map());
  const [busy, setBusy] = useState(false);
  const latest = useRef(p);
  latest.current = p;
  const modeNow = useRef(mode);
  modeNow.current = mode;
  const all = useMemo(() => [...p.categories, ...proposals], [p.categories, proposals]);

  /** Покупки без категории → предложения. «Класть в «Прочее»» модель не зовёт. */
  const suggest = async (rows: readonly { n: number; name: string }[]): Promise<void> => {
    if (rows.length === 0 || modeNow.current === 'other') return;
    setBusy(true);
    const got = await suggestCategories(rows.map((r) => r.name), modelCatalog(latest.current.categories), SUGGEST_ICONS);
    setBusy(false);
    if (!got) return;
    const { ids, proposals: fresh } = planSuggestions(got, latest.current.categories);
    const now = new Map<number, string>();
    const wait = new Map<number, string>();
    rows.forEach((r, k) => {
      const id = ids[k];
      if (!id || latest.current.touched(r.n)) return;
      // Категория, которая уже есть, — просто подсказка модели, не новая.
      if (!isNewCategory(id) || modeNow.current === 'auto') now.set(r.n, id);
      else wait.set(r.n, id);
    });
    if (fresh.length > 0) setProposals((prev) => [...prev, ...fresh.filter((f) => !prev.some((x) => x.id === f.id))]);
    if (now.size > 0) latest.current.apply(now);
    if (wait.size > 0) {
      setPending((prev) => new Map([...prev, ...wait]));
      if (modeNow.current === undefined) setAsking(true);
    }
  };

  /** Ответ на вопрос. Закрыли, не ответив, — в этой проверке спрашиваем по строкам, а ответа не храним. */
  const answer = (m: NewCategoryMode | null): void => {
    setAsking(false);
    if (m === null) { setMode('ask'); return; }
    setMode(m);
    void setNewCategoryMode(m);
    if (m === 'auto') {
      latest.current.apply(new Map([...pending].filter(([n]) => !latest.current.touched(n))));
      setPending(new Map());
    } else if (m === 'other') {
      setPending(new Map());
    }
  };

  /** «Создать «Спорт»» или «Выбрать свою» — для всех строк с этим предложением. */
  const settle = (key: string, id: string): void => {
    latest.current.apply(new Map([...pending].filter(([, k]) => k === key).map(([n]) => [n, id])));
    setPending((prev) => new Map([...prev].filter(([, k]) => k !== key)));
  };
  /** Человек сам выбрал категорию строки — предложение ей больше не нужно. */
  const resolve = (n: number): void => setPending((prev) => {
    if (!prev.has(n)) return prev;
    const next = new Map(prev);
    next.delete(n);
    return next;
  });
  /** Строку удалили: номера ниже сдвигаются. */
  const removeRow = (n: number): void => setPending((prev) =>
    new Map([...prev].filter(([i]) => i !== n).map(([i, k]) => [i > n ? i - 1 : i, k])));
  /** Новая категория не подошла — всем её строкам другая. */
  const replace = (key: string, id: string): void => {
    if (key === id) return;
    latest.current.apply(new Map(latest.current.ids.flatMap((x, n) => (x === key ? [[n, id] as const] : []))));
  };
  /**
   * Перед записью: создать новые категории, которые достались записываемым
   * строкам, и вернуть, чем заменить ключ `new:`.
   */
  const materialize = async (ids: readonly string[]): Promise<(id: string) => string> => {
    const real = await createProposals(proposals.filter((x) => ids.includes(x.id)));
    return (id) => {
      const out = real.get(id) ?? id;
      if (isNewCategory(out)) throw new Error('Новая категория не создалась — выбери другую');
      return out;
    };
  };

  return {
    mode, asking, answer, proposals, all, pending, busy, suggest, settle, resolve, removeRow, replace, materialize,
    onCategories: p.onCategories,
  };
}

export type NewCategories = ReturnType<typeof useNewCategories>;

export const NewTag = () => <span className="newtag">новая</span>;

/** Куда входит новая категория и куда пойдут её траты: «в «Здоровье» · повседневные нужды». */
export function whereOf(p: Proposal, all: readonly Category[], sep = ' · '): string {
  const parent = p.parentId ? all.find((c) => c.id === p.parentId) : undefined;
  return [parent ? `в «${parent.name}»` : null, PART_WORD[p.defaultPart]].filter(Boolean).join(sep);
}

/** Сколько записываемых строк у каждой новой категории; без строк её нет. */
const usedOf = (nc: NewCategories, ids: readonly string[]) => nc.proposals
  .map((p) => ({ p, n: ids.filter((x) => x === p.id).length }))
  .filter((x) => x.n > 0);

/** Выбор «как поступать» — при первом случае и из «Ещё → Категории». */
export function NewModeSheet({ title, lead, current, note = true, onPick, onClose }: {
  title: string;
  lead?: string;
  current?: NewCategoryMode | undefined;
  /** Где поменять ответ потом. Из самой настройки — не нужно. */
  note?: boolean;
  onPick: (m: NewCategoryMode) => void;
  onClose: () => void;
}) {
  return (
    <SheetWrap title={title} onClose={onClose}>
      {lead && <p className="asktext">{lead}</p>}
      <div className="fieldlist account-list">
        {(['auto', 'ask', 'other'] as const).map((m) => (
          <button key={m} className="li w" aria-pressed={current === m} onClick={() => onPick(m)}>
            <span className="nm"><b>{MODE_LABEL[m]}</b><span className="wrap">{MODE_SUB[m]}</span></span>
            <span>{current === m ? <Icon name="check" size={14} /> : null}</span>
          </button>
        ))}
      </div>
      {note && <p className="sheetnote">Изменить эту настройку можно в «Настройках → Категории» — шестерёнка в шапке главной.</p>}
    </SheetWrap>
  );
}

/** Вопрос, который задаётся один раз — когда впервые нашлись покупки без категории. */
export function NewModeQuestion({ nc, names }: { nc: NewCategories; names: readonly string[] }) {
  if (!nc.asking) return null;
  return (
    <NewModeSheet title="Нашлись покупки без подходящей категории"
      lead={`Для ${namesText(names)} подходящей категории нет. Как быть с такими дальше?`}
      onPick={nc.answer} onClose={() => nc.answer(null)} />
  );
}

/**
 * «Новые категории · N» — над операциями выписки: видно разом, что
 * появится. Тап по категории — выбрать вместо неё свою.
 */
export function NewCatsBlock({ nc, ids, unit }: {
  nc: NewCategories;
  /** Категории записываемых строк. */
  ids: readonly string[];
  unit: readonly [string, string, string];
}) {
  const used = usedOf(nc, ids);
  if (used.length === 0) return null;
  return (
    <div className="blk newcats">
      <div className="blkhead"><b>Новые категории · {used.length}</b></div>
      {used.map(({ p, n }) => (
        <CategoryField key={p.id} categories={nc.all} kind="expense" value={p.id} onUpdated={nc.onCategories}
          onChange={(id) => nc.replace(p.id, id)}
          trigger={(open) => (
            <button className="li w" onClick={open} aria-label={`Новая категория «${p.name}»: выбрать другую`}>
              <CategoryGlyph icon={p.icon} color={colorOf(p)} />
              <span className="nm"><b>{p.name}</b><span>{whereOf(p, nc.all)} · {plural(n, ...unit)}</span></span>
              <span className="ch"><Icon name="chevron-right" size={13} /></span>
            </button>
          )} />
      ))}
      <p className="newnote">Появятся, когда нажмёшь «Записать». Не подходит — нажми на категорию и выбери свою: тогда новая не появится.</p>
    </div>
  );
}

/** Строка над позициями чека: «Новая категория Декор — в «Дом и быт», повседневные нужды». */
export function NewCatsLines({ nc, ids }: { nc: NewCategories; ids: readonly string[] }) {
  return (
    <>
      {usedOf(nc, ids).map(({ p }) => (
        <div className="rcnewline" key={p.id}>
          <CategoryGlyph icon={p.icon} color={colorOf(p)} size="s" />
          <span>Новая категория <b>{p.name}</b> — {whereOf(p, nc.all, ', ')}. Появится, когда нажмёшь «Записать». Выберешь у позиции свою — не появится.</span>
        </div>
      ))}
    </>
  );
}

/**
 * «Нет подходящей категории · N» — у «Спрашивать каждый раз». Одно
 * предложение на категорию: пять абонементов в зал — одно решение.
 */
export function NewCatsAsk({ nc, nameOf, on }: {
  nc: NewCategories;
  nameOf: (n: number) => string;
  /** Строка записывается: снятые с галочки не спрашиваем. */
  on?: (n: number) => boolean;
}) {
  const groups = new Map<string, number[]>();
  for (const [n, key] of nc.pending) if (!on || on(n)) groups.set(key, [...(groups.get(key) ?? []), n]);
  const count = [...groups.values()].reduce((a, x) => a + x.length, 0);
  if (count === 0) return null;
  return (
    <div className="blk newcats">
      <div className="blkhead"><b>Нет подходящей категории · {count}</b></div>
      {[...groups].map(([key, rows]) => {
        const p = nc.proposals.find((x) => x.id === key);
        if (!p) return null;
        const parent = p.parentId ? nc.all.find((c) => c.id === p.parentId) : undefined;
        return (
          <div className="ncrow" key={key}>
            <div className="nchead">
              <CategoryGlyph icon={p.icon} color={colorOf(p)} />
              <div className="ncnm"><b>{namesText(rows.map(nameOf))}</b>
                <p>Подойдёт новая: <b>{p.name}</b>{parent ? ` в «${parent.name}»` : ''}</p></div>
            </div>
            <div className="ncbtns">
              <button className="btn pri sm" onClick={() => nc.settle(key, key)}>Создать «{p.name}»</button>
              <CategoryField categories={nc.all.filter((c) => c.id !== key)} kind="expense" value=""
                onUpdated={nc.onCategories} onChange={(id) => nc.settle(key, id)}
                trigger={(open) => <button className="btn sm" onClick={open}>Выбрать свою</button>} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Строка настройки в «Ещё → Категории». */
export function NewCatsModeRow({ mode, onChange }: {
  mode: unknown;
  onChange: (m: NewCategoryMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(modeOf(mode));
  return (
    <>
      {/* Настройка — карточкой со значением справа (28.10.2026): строкой списка
          она читалась как ещё одна категория. */}
      <button className="catauto" onClick={() => setOpen(true)}>
        <span className="catico c-gray"><Icon name="receipt" size={18} /></span>
        <span className="nm"><b>Новая покупка в чеке или выписке</b>
          <span className="catauto-val">{now ? MODE_LABEL[now] : 'Не выбрано'}</span></span>
        <Icon name="chevron-right" size={13} />
      </button>
      {open && (
        <NewModeSheet title="Новые категории из чеков и выписок" current={now} note={false}
          onPick={(m) => { setNow(m); setOpen(false); onChange(m); }} onClose={() => setOpen(false)} />
      )}
    </>
  );
}
